import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { UserPlus, Search, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { listStudents, listParents } from "@/lib/api/students.functions";

export const Route = createFileRoute("/_authenticated/m/cadastro-resp-aluno")({
  component: CadastroRespAlunoPage,
});

type Parent = {
  id: string;
  name: string;
  cpf: string | null;
  phone: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  street: string | null;
  number: string | null;
  user_id: string | null;
};

function CadastroRespAlunoPage() {
  const listP = useServerFn(listParents);
  const listS = useServerFn(listStudents);

  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState<Parent[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [p, s] = await Promise.all([listP(), listS()]);
        setParents((p.parents ?? []) as unknown as Parent[]);
        setStudents((s.students ?? []) as any[]);
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar responsáveis");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const studentsByParent = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const s of students) {
      const pid = s.parents?.id ?? s.parent_id;
      if (!pid) continue;
      const arr = map.get(pid) ?? [];
      arr.push(s.name);
      map.set(pid, arr);
    }
    return map;
  }, [students]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parents;
    return parents.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.cpf ?? "").includes(q) ||
        (p.phone ?? "").includes(q),
    );
  }, [parents, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <UserPlus className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cadastro Resp./Aluno</h1>
          <p className="text-muted-foreground text-sm">
            Responsáveis cadastrados e os alunos vinculados a cada um.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Buscar responsável por nome, CPF ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Button asChild variant="outline">
          <Link to="/m/alunos">Ir para alunos</Link>
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border bg-card p-10 text-center text-muted-foreground">
          Nenhum responsável encontrado.
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p) => {
            const kids = studentsByParent.get(p.id) ?? [];
            return (
              <Card key={p.id}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-11 w-11">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {p.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <p className="font-semibold leading-tight truncate">{p.name}</p>
                      {p.cpf && <p className="text-xs text-muted-foreground">CPF/CNPJ: {p.cpf}</p>}
                    </div>
                  </div>

                  <div className="space-y-1 text-xs text-muted-foreground">
                    {p.phone && (
                      <p className="flex items-center gap-1"><Phone className="h-3 w-3" /> {p.phone}</p>
                    )}
                    {p.email && (
                      <p className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" /> {p.email}</p>
                    )}
                    {(p.city || p.street) && (
                      <p className="flex items-center gap-1 truncate">
                        <MapPin className="h-3 w-3" />
                        {[p.street, p.number, p.city, p.state].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-1">
                    <Badge variant={p.user_id ? "default" : "outline"} className={p.user_id ? "bg-emerald-600 hover:bg-emerald-600" : ""}>
                      {p.user_id ? "Conta ativa" : "Sem conta de acesso"}
                    </Badge>
                    <Badge variant="secondary">{kids.length} aluno(s)</Badge>
                  </div>

                  {kids.length > 0 && (
                    <div className="border-t pt-2 space-y-0.5">
                      {kids.map((n) => (
                        <p key={n} className="text-xs">{n}</p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
